import {
    PUZZLE_FOCUS_COMMENT_ID_KEY,
    PUZZLE_FOCUS_COMMENT_PATH_KEY,
    PUZZLE_OPEN_SCOREBOARD_KEY,
} from "@/constants/puzzle-storage.constants";
import { appendPuzzleViewToPath } from "@/helpers/puzzle-view-url";
import { encodePuzzleLonglinkInUrl, withCurrentSearchParams } from "@/helpers/withCurrentSearchParams";

function resolvePathnameFromNavigationPath(path: string): string {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const queryIndex = normalized.indexOf("?");
    const hashIndex = normalized.indexOf("#");
    const end = Math.min(
        queryIndex === -1 ? normalized.length : queryIndex,
        hashIndex === -1 ? normalized.length : hashIndex,
    );
    return normalized.slice(0, end);
}

export type NavigateToPuzzleScoreboardParams = {
    navigate: (path: string) => void;
    shortLink?: string | null;
    longLink?: string | null;
    focusCommentId?: string | null;
};

/**
 * Navigates to a puzzle scoreboard from an in-app notification.
 * Prefers longLink (avoids short-url redirect clearing session flags). Falls back to shortLink + session flag.
 */
export function navigateToPuzzleScoreboardFromNotification({
    navigate,
    shortLink,
    longLink,
    focusCommentId,
}: NavigateToPuzzleScoreboardParams): boolean {
    const trimmedShortLink = shortLink?.trim() ?? "";
    const trimmedLongLink = longLink?.trim() ?? "";
    if (!trimmedShortLink && !trimmedLongLink) {
        return false;
    }
    let navigationPath = "";
    if (trimmedLongLink) {
        const basePath = trimmedLongLink.startsWith("/")
            ? `/puzzle${trimmedLongLink}`
            : `/puzzle/${trimmedLongLink}`;
        const encodedPath = encodePuzzleLonglinkInUrl(basePath);
        navigationPath = appendPuzzleViewToPath(withCurrentSearchParams(encodedPath), "scoreboard");
    } else {
        navigationPath = withCurrentSearchParams(`/puzzle/${encodeURIComponent(trimmedShortLink)}`);
    }
    try {
        sessionStorage.setItem(PUZZLE_OPEN_SCOREBOARD_KEY, "true");
        sessionStorage.removeItem(PUZZLE_FOCUS_COMMENT_ID_KEY);
        sessionStorage.removeItem(PUZZLE_FOCUS_COMMENT_PATH_KEY);
        if (focusCommentId) {
            sessionStorage.setItem(PUZZLE_FOCUS_COMMENT_ID_KEY, String(focusCommentId));
            sessionStorage.setItem(
                PUZZLE_FOCUS_COMMENT_PATH_KEY,
                resolvePathnameFromNavigationPath(navigationPath),
            );
        }
    } catch {
        /* ignore */
    }
    navigate(navigationPath);
    return true;
}
